import type { JSX, KeyboardEvent } from 'react';
import { useEffect, useRef } from 'react';
import type { OtpAction, OtpState } from './otp';

/**
 * Six numeric boxes. Typing moves on, Backspace moves back, the arrows walk,
 * and a pasted or autofilled code lands in every box at once. The caret
 * follows `state.focus`, which the reducer decides. Enter submits the form.
 */
export function OtpBoxes({
  state,
  onAction,
}: {
  state: OtpState;
  onAction: (action: OtpAction) => void;
}): JSX.Element {
  const refs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    refs.current[state.focus]?.focus();
  }, [state]);

  const onKey = (index: number) => (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace') {
      event.preventDefault();
      onAction({ type: 'backspace', index });
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      onAction({ type: 'move', index, by: event.key === 'ArrowLeft' ? -1 : 1 });
    }
  };

  return (
    <div className="grid grid-cols-6 gap-2">
      {state.digits.map((digit, index) => (
        <input
          key={index}
          ref={(el) => {
            refs.current[index] = el;
          }}
          value={digit}
          inputMode="numeric"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          aria-label={`Digit ${index + 1}`}
          onFocus={(event) => event.target.select()}
          onChange={(event) => onAction({ type: 'input', index, raw: event.target.value })}
          onKeyDown={onKey(index)}
          onPaste={(event) => {
            event.preventDefault();
            onAction({ type: 'input', index, raw: event.clipboardData.getData('text') });
          }}
          className="bg-bg w-full rounded-[11px] border py-3.5 text-center font-mono text-xl transition-colors"
          style={{
            borderColor:
              digit === '' ? 'var(--line)' : 'color-mix(in oklch,var(--acc) 45%,transparent)',
          }}
        />
      ))}
    </div>
  );
}
